import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { buildProductPath } from '../utils/productSlug';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import Loading from './Loading';
import ProductGridItem from './ProductGridItem';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import {
  MAX_CONTENT_WIDTH,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
} from '../constants/layout';
import { FONT_TITLE2, FONT_TITLE2_MOBILE } from '../constants/typography';
import { ProductsContext } from '../contexts/ProductsContext';
import SortPicker, { sortProducts } from '../components/SortPicker';

const parseDate = value =>
  !value ? null : value.toDate ? value.toDate() : new Date(value);

const isProductActive = product => {
  const deactivatedDate = parseDate(product?.deactivatedDate);
  if (!deactivatedDate) return true;
  const today = new Date();
  return today <= deactivatedDate;
};

export default function CategoryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, getCoverImage } = useContext(ProductsContext);
  const { showUncategorized } = useDisplayPreferences();
  const [category, setCategory] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [loadingCategory, setLoadingCategory] = useState(true);
  const [sortBy, setSortBy] = useState('name_asc');

  const isMore = id === 'more';

  useEffect(() => {
    let isMounted = true;

    if (isMore) {
      // Fetch all categories to compute uncategorized products
      (async () => {
        try {
          const snap = await getDocs(collection(db, 'categories'));
          if (!isMounted) return;
          setAllCategories(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          if (showUncategorized) {
            setCategory({ id: 'more', title: 'More' });
          } else {
            setCategory(null);
          }
        } finally {
          if (isMounted) setLoadingCategory(false);
        }
      })();
    } else {
      (async () => {
        if (!id) return;
        try {
          const ref = doc(db, 'categories', id);
          const snap = await getDoc(ref);
          if (!isMounted) return;
          if (snap.exists()) {
            setCategory({ id: snap.id, ...snap.data() });
          } else {
            setCategory(null);
          }
        } finally {
          if (isMounted) setLoadingCategory(false);
        }
      })();
    }

    return () => {
      isMounted = false;
    };
  }, [id, isMore, showUncategorized]);

  useEffect(() => {
    if (category?.title) {
      document.title = category.title;
      return () => {
        document.title = 'Catalog';
      };
    }
    return undefined;
  }, [category?.title]);

  const categoryProducts = useMemo(() => {
    if (isMore) {
      const allCategorizedIDs = new Set(allCategories.flatMap(c => c.itemIDs || []));
      const filtered = products
        .filter(prod => !allCategorizedIDs.has(prod.id))
        .filter(isProductActive);
      return sortProducts(filtered, sortBy);
    }
    const categoryProductIds = Array.isArray(category?.itemIDs) ? category.itemIDs : [];
    const filtered = products
      .filter(prod => categoryProductIds.includes(prod.id))
      .filter(isProductActive);
    return sortProducts(filtered, sortBy);
  }, [isMore, allCategories, products, category?.itemIDs, sortBy]);

  if (loadingCategory) {
    return <Loading />;
  }

  if (!category) {
    return (
      <div className="category-detail-container">
        <div className="category-header">
          <h1 className="category-title">Category not found</h1>
          <p className="category-description">
            We couldn't find the category you're looking for.
          </p>
        </div>
        <style jsx>{`
          .category-detail-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: ${SPACING_CARD_BODY} ${SPACING_CARD} ${SPACING_SECTION};
            padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
            padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
          }

          .category-header {
            margin-bottom: ${SPACING_CARD};
          }

          .category-title {
            font-size: 2rem;
            font-family: var(--font-family-base);
            font-weight: 600;
            margin: 0;
          }

          .category-description {
            color: var(--color-text-secondary);
            margin-top: ${SPACING_COMPONENT};
          }

        `}</style>
      </div>
    );
  }

  return (
    <div className="category-detail-container">
      <div className="category-header">
        <div className="category-title-row">
          <h1 className="category-title">{category.title}</h1>
          {categoryProducts.length > 0 && (
            <SortPicker value={sortBy} onChange={setSortBy} />
          )}
        </div>
        {typeof category.subtitle === 'string' && category.subtitle.trim() ? (
          <p className="category-subtitle">{category.subtitle}</p>
        ) : null}
        {category.description ? (
          <p className="category-description">{category.description}</p>
        ) : null}
      </div>

      <div className="products-wrapper">
        {categoryProducts.length === 0 ? (
          <p className="empty-state">No products are currently available in this category.</p>
        ) : (
          <div className="product-grid">
            {categoryProducts.map(product => (
              <ProductGridItem
                key={product.id}
                product={product}
                coverImage={getCoverImage(product)}
                onClick={() => navigate(buildProductPath(product))}
                isNew={(() => {
                  if (!product?.createdDate) return false;
                  const created = product.createdDate.toDate ? product.createdDate.toDate() : new Date(product.createdDate);
                  const thirtyDaysAgo = new Date();
                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                  return created > thirtyDaysAgo;
                })()}
              />
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .category-detail-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_CARD_BODY} ${SPACING_CARD} ${SPACING_SECTION};
          padding-left: max(${SPACING_CARD}, env(safe-area-inset-left));
          padding-right: max(${SPACING_CARD}, env(safe-area-inset-right));
        }

        .category-header {
          margin-bottom: ${SPACING_CARD};
        }

        .category-title {
          font-size: ${FONT_TITLE2};
          font-family: var(--font-family-base);
          font-weight: 600;
          margin: 0;
        }

        .category-subtitle {
          margin: 4px 0 0;
          color: var(--color-text-secondary);
        }

        .category-description {
          color: var(--color-text-secondary);
          margin-top: ${SPACING_COMPONENT};
          line-height: 1.6;
        }

        .products-wrapper {
          margin-bottom: ${SPACING_SECTION};
        }

        .category-title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: ${SPACING_BUTTON};
        }

        .empty-state {
          color: var(--color-text-muted);
        }

        .product-grid {
          display: grid;
          width: 100%;
          gap: ${SPACING_BUTTON};
          grid-template-columns: repeat(4, 1fr);
        }
        @media (min-width: 1400px) {
          .product-grid { grid-template-columns: repeat(5, 1fr); }
        }
        @media (min-width: 1000px) and (max-width: 1399px) {
          .product-grid { grid-template-columns: repeat(4, 1fr); }
        }
        @media (max-width: 999px) {
          .product-grid { grid-template-columns: repeat(3, 1fr); }
        }
        @media (max-width: 600px) {
          .category-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }
          .product-grid { grid-template-columns: repeat(2, 1fr); }
        }
      `}</style>
    </div>
  );
}
